import type { TrackCompletedAttemptMoneyEvent } from "@circle-react/api/conversionTrackingApi";
import { useCheckoutSession } from "@circle-react/components/Paywalls/Checkout/contexts/CheckoutSessionContext";
import { useConversionTrackingContext } from "@circle-react/contexts/ConversionTrackingContext";
import {
  useScopedNavigate,
  useScopedParams,
} from "@circle-react/routes/PaywallCheckout/paywallCheckoutRouterHelpers";
import { useCallback } from "react";

import type { CheckoutSession } from "@/react/types/Paywalls/Checkout/Session";

import { firePurchaseForCompletedSteps } from "../helpers/ga4PurchaseHelper";
import {
  getMainPaywall,
  getNextAvailableOfferAfterMain,
  getRedirectUrlFromMainPaywallAndPaywallPrice,
} from "../helpers/SessionStepQueryHelper";

/**
 * Hook for navigating to the next session step (offer or confirmation).
 *
 * Used by offer pages to navigate after payment completion or decline.
 * Returns false if navigation cannot be performed (no session, no checkoutPath,
 * or upsells disabled), allowing the consumer to handle fallback navigation.
 *
 * @returns goToNextStep(session, stepId?) - pass stepId when navigating from
 *          a specific offer, omit when navigating from the main offer.
 *          Returns true if navigation occurred, false if `session` is
 *          missing or there is no checkoutPath.
 * @returns redirectAfterConfirmation(session) - redirects to the community
 *          URL or the custom URL.
 */
export const useGoToNextStep = () => {
  const navigate = useScopedNavigate();
  const params = useScopedParams({ strict: false });
  const checkoutPath = params?.checkoutPath;
  const { ga4FiredStepIdsRef } = useCheckoutSession();
  const { track } = useConversionTrackingContext();

  const triggerEventPurchaseAndRedirect = (url: string) => {
    window.webview?.onEventPurchaseEnded?.();
    location.replace(url);
  };

  const goToNextStep = useCallback(
    (
      currentSession: CheckoutSession | null,
      stepId?: number,
      moneyEvent?: TrackCompletedAttemptMoneyEvent | null,
    ) => {
      if (moneyEvent) track(moneyEvent);
      // Session-based offer navigation requires checkoutPath and session
      if (!checkoutPath || !currentSession) {
        return false;
      }

      // Check for next offer
      const nextStep = getNextAvailableOfferAfterMain(
        currentSession.steps,
        stepId,
      );

      if (nextStep) {
        void navigate({
          to: "/checkout/$checkoutPath/step/$stepId",
          params: {
            checkoutPath,
            stepId: String(nextStep.id),
          },
          replace: true,
        });
        return true;
      }

      const paywall = getMainPaywall(currentSession);
      const redirectUrl =
        getRedirectUrlFromMainPaywallAndPaywallPrice(currentSession);

      if (paywall?.thank_you_page_enabled === false && redirectUrl) {
        // Fire GA4 purchase events before replacing the page. ConfirmationPage
        // is never rendered in this path so this is the only emission point.
        // The main step's GA4 event was already fired and marked in
        // AttemptsProvider.onComplete, so firePurchaseForCompletedSteps is a
        // dedup-guarded no-op for that step; it fires any steps not yet covered.
        firePurchaseForCompletedSteps(currentSession, ga4FiredStepIdsRef);
        triggerEventPurchaseAndRedirect(redirectUrl);
        return true;
      }

      // No more offers, go to confirmation
      void navigate({
        to: "/checkout/$checkoutPath/confirmation/$sessionId",
        params: {
          checkoutPath,
          sessionId: String(currentSession.id),
        },
        replace: true,
      });
      return true;
    },
    [checkoutPath, navigate, ga4FiredStepIdsRef, track],
  );

  const redirectAfterConfirmation = useCallback(
    (currentSession: CheckoutSession | null) => {
      const redirectUrl =
        getRedirectUrlFromMainPaywallAndPaywallPrice(currentSession);

      if (!redirectUrl) {
        return;
      }

      triggerEventPurchaseAndRedirect(redirectUrl);
    },
    [],
  );

  return { goToNextStep, redirectAfterConfirmation };
};
